function decodeJwtPayload(token) {
  const part = String(token).split(".")[1];
  if (!part) {
    throw new Error("Invalid token");
  }
  const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  return JSON.parse(atob(padded));
}

/**
 * Installs tokens from the marketplace auth API onto the existing Keycloak client.
 * Tokens stay in memory. Calls onAuthSuccess so the React provider and token refresh start.
 *
 * @param {import("keycloak-js").default} keycloak
 * @param {{ accessToken?: string, refreshToken?: string, idToken?: string, access_token?: string, refresh_token?: string, id_token?: string }} tokens
 */
export function adoptKeycloakSession(keycloak, tokens) {
  const accessToken = tokens?.accessToken ?? tokens?.access_token;
  const refreshToken = tokens?.refreshToken ?? tokens?.refresh_token;
  const idToken = tokens?.idToken ?? tokens?.id_token;
  if (!accessToken || !idToken) {
    throw new Error("Sign-in did not return a session.");
  }

  const tokenParsed = decodeJwtPayload(accessToken);
  keycloak.token = accessToken;
  keycloak.tokenParsed = tokenParsed;
  keycloak.idToken = idToken;
  keycloak.idTokenParsed = decodeJwtPayload(idToken);
  if (refreshToken) {
    keycloak.refreshToken = refreshToken;
    keycloak.refreshTokenParsed = decodeJwtPayload(refreshToken);
  }
  keycloak.authenticated = true;
  keycloak.subject = tokenParsed.sub;
  keycloak.sessionId = tokenParsed.session_state;
  keycloak.realmAccess = tokenParsed.realm_access;
  keycloak.resourceAccess = tokenParsed.resource_access;
  const nowSeconds = Math.floor(Date.now() / 1000);
  keycloak.timeSkew =
    typeof tokenParsed.iat === "number" ? nowSeconds - tokenParsed.iat : 0;
  if (typeof keycloak.onAuthSuccess === "function") {
    keycloak.onAuthSuccess();
  }
}

/**
 * Seconds until the access token expires, using the same clock skew keycloak-js uses.
 * @param {{ tokenParsed?: { exp?: number }, timeSkew?: number }} keycloak
 * @param {number} [nowMs]
 */
export function accessTokenSecondsRemaining(keycloak, nowMs = Date.now()) {
  const exp = keycloak?.tokenParsed?.exp;
  if (typeof exp !== "number") return 0;
  const skew = typeof keycloak?.timeSkew === "number" ? keycloak.timeSkew : 0;
  return exp - Math.ceil(nowMs / 1000) + skew;
}

/**
 * In-app path to return to after sign-in. Rejects external and protocol-relative URLs.
 * @param {unknown} value
 */
export function safeReturnPath(value) {
  if (typeof value !== "string") return "/";
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  if (value.startsWith("/login") || value.startsWith("/complete-registration")) return "/";
  return value;
}
